/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/icon/icon.js';
import '../../components/circle-button';
import { showConfirmation } from '../../components/confirm-dialog';
import { vibrate } from '../common/haptics';
import { RowCounterState, getCount, getCounter, getRepeats } from '../data/counter-model';
import {
  addCounterListener,
  getCounterState,
  incrementCounter,
  removeCounterListener,
  resetCounter,
} from '../data/counter-store';

/**
 * Large view of a single counter. Fires `edit-counter` with the counter ID
 * when the edit button is pressed.
 */
@customElement('yl-counter-view')
export class CounterView extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    h2 {
      margin: 8px 48px 0;
      font-size: 1.1rem;
      font-weight: 500;
      overflow-wrap: anywhere;
    }

    .count {
      font-family: var(--yl-headline-font);
      font-size: clamp(5rem, 22vw, 7rem);
      line-height: 1;
      margin: 32px 0 24px;
      font-variant-numeric: tabular-nums;
    }

    :host([hide-title]) .count {
      margin-top: 8px;
    }

    .buttons {
      display: flex;
      gap: 32px;
    }

    .details {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 20px;
      font-size: 0.9rem;
      font-weight: 500;
      color: var(--yl-secondary-text);
    }

    .details div {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .actions {
      display: flex;
      justify-content: space-between;
      align-self: stretch;
      margin-top: 16px;
    }
  `;

  @property({ attribute: 'counter-id' })
  counterId: string = '';

  @property({ attribute: 'hide-title', type: Boolean, reflect: true })
  hideTitle: boolean = false;

  @state()
  private _state: RowCounterState = getCounterState();

  private onCountersChangedCallback = this.onCountersChanged.bind(this);

  private onCountersChanged(state: RowCounterState) {
    this._state = state;
  }

  connectedCallback() {
    super.connectedCallback();
    this._state = getCounterState();
    addCounterListener(this.onCountersChangedCallback);
  }

  disconnectedCallback() {
    removeCounterListener(this.onCountersChangedCallback);
    super.disconnectedCallback();
  }

  private _increment(delta: number) {
    if (incrementCounter(this.counterId, delta)) vibrate();
  }

  private async _reset() {
    const confirmed = await showConfirmation({
      message: 'Are you sure you want to reset the row count?',
      confirmText: 'Reset',
      destructive: true,
    });
    if (confirmed) resetCounter(this.counterId);
  }

  private _edit() {
    this.dispatchEvent(
      new CustomEvent<string>('edit-counter', { detail: this.counterId, bubbles: true, composed: true }),
    );
  }

  render() {
    const state = this._state;
    const counter = getCounter(state, this.counterId);
    if (counter === null) {
      return html``;
    }
    const parent = counter.parentId !== null ? getCounter(state, counter.parentId) : null;
    return html`
      ${this.hideTitle ? null : html`<h2>${counter.label || 'Row Counter'}</h2>`}
      <div class="count" aria-live="polite" aria-atomic="true">${getCount(state, counter.id)}</div>
      <div class="buttons">
        <yl-circle-button
          icon="minus"
          label="Decrement counter"
          size="large"
          activate-on-press
          @activate=${() => this._increment(-1)}
        ></yl-circle-button>
        <yl-circle-button
          icon="plus"
          label="Increment counter"
          size="large"
          primary
          activate-on-press
          @activate=${() => this._increment(1)}
        ></yl-circle-button>
      </div>
      ${counter.rowsInRepeat !== null || parent !== null
        ? html`<div class="details">
            ${counter.rowsInRepeat !== null
              ? html`<div>
                  <wa-icon name="retweet"></wa-icon>
                  ${plural(counter.rowsInRepeat, 'Every # row', 'Every # rows')} •
                  ${plural(getRepeats(state, counter.id), '# repeat', '# repeats')}
                </div>`
              : null}
            ${parent !== null
              ? html`<div>
                  <wa-icon name="link"></wa-icon>
                  ${parent.label || 'Untitled counter'} • ${getCount(state, parent.id)}
                </div>`
              : null}
          </div>`
        : null}
      <div class="actions">
        <yl-circle-button icon="rotate-left" label="Reset" @activate=${this._reset}></yl-circle-button>
        <yl-circle-button icon="pen" label="Edit" @activate=${this._edit}></yl-circle-button>
      </div>
    `;
  }
}

function plural(count: number, one: string, other: string): string {
  return (count === 1 ? one : other).replace('#', `${count}`);
}

declare global {
  interface HTMLElementTagNameMap {
    'yl-counter-view': CounterView;
  }
}
